import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";

export default function FieldMappingsPage() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", source_field: "", target_field: "", transform: "none", notes: "" });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await API.get("/field-mappings");
      setMappings(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await API.put(`/field-mappings/${editing}`, form);
      } else {
        await API.post("/field-mappings", form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", source_field: "", target_field: "", transform: "none", notes: "" });
      load();
    } catch (e) { alert("Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this mapping?")) return;
    try { await API.delete(`/field-mappings/${id}`); load(); } catch { alert("Delete failed"); }
  };

  const openEdit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name, source_field: m.source_field, target_field: m.target_field, transform: m.transform, notes: m.notes || "" });
    setShowForm(true);
  };

  const TRANSFORMS = ["none", "trim", "uppercase", "lowercase", "phone_normalize", "date_iso", "strip_non_numeric", "status_map"];

  return (
    <div data-testid="field-mappings-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Field Mappings</h1>
          <p className="text-sm text-zinc-500 mt-1">Define how panel fields map to canonical fields</p>
        </div>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-mapping-btn" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-2"><Plus size={16} /> Add Mapping</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Field Mapping</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <FF label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="mapping-name" />
              <FF label="Source Field (from panel)" value={form.source_field} onChange={(v) => setForm({ ...form, source_field: v })} testId="mapping-source" />
              <FF label="Target Field (canonical)" value={form.target_field} onChange={(v) => setForm({ ...form, target_field: v })} testId="mapping-target" />
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Transform</Label>
                <select
                  data-testid="mapping-transform"
                  value={form.transform}
                  onChange={(e) => setForm({ ...form, transform: e.target.value })}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm"
                >
                  {TRANSFORMS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <FF label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="mapping-notes" />
              <Button data-testid="save-mapping-btn" onClick={handleSubmit} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">{editing ? "Update" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Name</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Source</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Target</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Transform</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
            ) : mappings.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-400">No field mappings configured</td></tr>
            ) : (
              mappings.map((m) => (
                <tr key={m.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`mapping-row-${m.id}`}>
                  <td className="px-4 py-2 text-sm text-zinc-900">{m.name}</td>
                  <td className="px-4 py-2 text-sm font-mono text-zinc-600">{m.source_field}</td>
                  <td className="px-4 py-2 text-sm font-mono text-zinc-600">{m.target_field}</td>
                  <td className="px-4 py-2 text-xs font-mono text-zinc-500 uppercase">{m.transform}</td>
                  <td className="px-4 py-2 flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)} className="text-zinc-400 hover:text-red-600"><Trash size={14} /></Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FF({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm border-zinc-300" />
    </div>
  );
}
